'use client';

/**
 * The ringing tone, with exactly one owner.
 *
 * Two things want to ring: the wake word, from the moment it hears its name,
 * and the mic button, from the tap. They can also happen in sequence — the wake
 * word starts ringing during the microphone handoff, and a moment later the
 * session it opened reports itself as connecting.
 *
 * 🚨 So the guard against a double ring is not "remember not to call it twice",
 * it is that a second `ringOn()` while ringing does nothing at all. Two
 * oscillator pairs a few hundred milliseconds out of phase do not sound like a
 * louder ring, they sound like a fault — and the ring exists precisely to say
 * that nothing is wrong.
 *
 * `ringOff()` is equally safe to call from either side, and both sides do:
 * whoever gets there first stops it, and the other is a no-op. That matters
 * because each has a failure the other cannot see — the wake word can abort
 * before a session is ever requested, and a session can fail to connect long
 * after the wake word has stopped caring.
 */
import { startRinging } from './sounds';
import { hasVoiceProof, VoiceProof, type RingCaller } from '@/lib/native/voice-proof';

let stop: (() => void) | null = null;
let cap: ReturnType<typeof setTimeout> | null = null;

/**
 * One long-lived AudioContext, kept running for the life of the page.
 *
 * 🚨 Not one per ring. Creating a context requires a user gesture on iOS or it
 * comes back SUSPENDED, and a suspended context schedules against a clock that
 * is not running — it plays silence and reports no error. The wake word rings
 * with no gesture anywhere near it, moments after we have just closed the
 * capture context, so every ring it asked for was inaudible while the mic
 * button's rang perfectly. Same class of bug as the wake word's own capture
 * context, one layer up.
 *
 * So: made once, resumed, and reused. `primeRinging()` gets it made during a
 * real tap; after that it stays running and every later ring is audible whoever
 * asks for it.
 */
let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx && ctx.state !== 'closed') return ctx;
  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return null;
  try {
    ctx = new AudioCtx();
  } catch {
    return null;
  }
  return ctx;
}

/**
 * The same long-lived context, for other cues that sound with no tap near them
 * (dictation's reconnecting pulse). Prime it with `primeRinging()` in the tap
 * that starts the thing that may later need it.
 */
export function sharedAudioContext(): AudioContext | null {
  return audioContext();
}

/**
 * Make and start the ringing context while a real user gesture is in progress.
 *
 * Call it from a tap handler — synchronously, before any await, the same rule
 * `unlockIOSAudio` follows and for the same reason. Everything after that point
 * is outside the gesture window as far as iOS is concerned.
 */
export function primeRinging(): void {
  const c = audioContext();
  if (c && c.state === 'suspended') void c.resume().catch(() => {});
}

/**
 * Nothing should ring for this long. A phone that rings for ever is worse than
 * one that never rings: the first is a fault you have to go and silence, the
 * second is a feature you assume is off.
 */
const MAX_RING_MS = 30_000;

/**
 * What the last ring actually did. Read by the wake-word status line.
 *
 * 🚨 Two fixes have now been shipped for "the wake word does not ring" on the
 * strength of a plausible story — a suspended context, then a context created
 * outside a gesture — and neither worked. Both stories were reasonable and
 * neither was checked, because nothing reports what happens. So: report it.
 * Whether ringOn was reached at all, what the context said when it did, and how
 * long the ring lasted, split the remaining candidates apart:
 *
 *   never called          → the wake-word effect is not running
 *   called, ctx suspended → still an audio-permission problem
 *   called, ctx running, ms tiny  → it rang and was cut off almost at once
 *   called, ctx running, ms long  → it played and was inaudible, which is an
 *                                   iOS routing question, not a code one
 */
export interface RingReport {
  calls: number;
  lastContextState: string | null;
  lastDurationMs: number | null;
}

let report: RingReport = { calls: 0, lastContextState: null, lastDurationMs: null };
let startedAt = 0;

export function getRingReport(): RingReport {
  return report;
}

export function isRinging(): boolean {
  return stop !== null || pending !== null;
}

/**
 * Non-null while a ring is waiting on `resume()`. Counts as ringing for both
 * `ringOn` (so a second caller cannot stack a tone) and `ringOff` (so a connect
 * that resolves inside the resume does not leave one to start behind it).
 */
let pending: { cancelled: boolean } | null = null;

/**
 * Whether this app plays the ring itself (native voice plugin v4+).
 *
 * 🚨 On the phone app the Web Audio ring was never heard. The native call takes
 * the audio session for voice within a second of the tap, and the WebView's
 * audio does not survive that, so Tom's own test call rang for nobody (7 Oct
 * 2026). Same ring, played by the app through the session the call is about to
 * use. Older binaries keep the page's ring. Unknown until the plugin answers,
 * which it does long before anyone taps; a tap that beats it rings in the page.
 */
/**
 * 🚨 ASK THE APP ON THE TAP, never only after a check made at page load. That
 * was the first version: `appRings` started false and flipped when
 * `capabilities()` answered, so a tap that beat the answer rang in the page —
 * silent on the phone app — and the call rang only once the app's own start
 * got going, a second or so later (Tom's test, 8 Oct 2026: "it should be as
 * soon as you press the button"). Now any native shell is asked straight
 * away; an older binary without `setRinging` rejects, and only then does the
 * page ring instead and stop asking.
 */
let appRings: boolean | null = null;
if (typeof window !== 'undefined') {
  try {
    if (hasVoiceProof()) void VoiceProof.capabilities().then(c => { appRings = c.version >= 4; }).catch(() => {});
    else appRings = false;
  } catch { appRings = false; }
}
let ringingInApp = false;

/**
 * Start ringing. Does nothing if it already is. `by` is passed to the app so a
 * ring iOS refuses can say who asked for it (MUCKA-IOS-P).
 */
export function ringOn(inPage = false, by?: RingCaller): void {
  if (stop || pending) return;
  let native = false;
  try { native = !inPage && appRings !== false && hasVoiceProof(); } catch { native = false; }
  if (native) {
    ringingInApp = true;
    const mine = () => { ringingInApp = false; void VoiceProof.setRinging({ on: false }).catch(() => {}); };
    stop = mine;
    startedAt = Date.now();
    report = { ...report, calls: report.calls + 1, lastContextState: 'app' };
    cap = setTimeout(ringOff, MAX_RING_MS);
    void VoiceProof.setRinging(by ? { on: true, by } : { on: true }).catch(() => {
      // An app from before `setRinging`: ring in the page, and stop asking.
      appRings = false;
      if (stop !== mine) return;
      stop = null; ringingInApp = false;
      if (cap) { clearTimeout(cap); cap = null; }
      ringOn(true);
    });
    return;
  }
  const c = audioContext();
  if (!c) return;
  // 🚨 A suspended context freezes `currentTime`, and `startRinging` schedules
  // its pair off that — so starting one now would put BOTH bursts in the past
  // by the time the clock runs, and they fire together as one doubled tone.
  // iOS suspends on background and does not resume on the way back, and since
  // the ring moved into the tap there is no render between the two to absorb
  // it. Wait for the resume and ring then: a few ms later than running, and
  // still the earliest a sound could be made.
  if (c.state === 'suspended') {
    const token = { cancelled: false };
    pending = token;
    c.resume()
      .catch(() => {})
      .finally(() => {
        if (pending === token) pending = null;
        if (token.cancelled) return;
        begin(c);
      });
    return;
  }
  begin(c);
}

function begin(c: AudioContext): void {
  if (stop) return;
  // Optional feedback must not prevent the voice connection if Web Audio is
  // unavailable or a device refuses a node after the context was created.
  try { stop = startRinging(c); } catch { return; }
  startedAt = Date.now();
  report = { ...report, calls: report.calls + 1, lastContextState: c.state };
  cap = setTimeout(ringOff, MAX_RING_MS);
}

/** Stop ringing. Does nothing if it is not. */
export function ringOff(): void {
  if (pending) { pending.cancelled = true; pending = null; }
  if (cap) { clearTimeout(cap); cap = null; }
  if (!stop) return;
  const s = stop;
  stop = null;
  report = { ...report, lastDurationMs: startedAt ? Date.now() - startedAt : null };
  s();
}

/**
 * The call is going to run in the PAGE after all (a native app with background
 * voice switched off), so the app's ring must hand back before the page asks
 * for the microphone: the app releases its audio session when its ring ends,
 * and doing that under a live WebRTC call would cut it.
 */
export function ringInPage(): void {
  if (!ringingInApp) return;
  ringOff();
  ringOn(true);
}
